/**
 * Network layer for the survey admin endpoints (`/api/admin/surveys/*`, the `[ADMIN]` half
 * of the SURVEYS collection).
 */

import axiosInstance from "../../utils/axiosConfig";
import { compactParams } from "./params";
import type {
  SurveyDetailEnvelope,
  SurveyMutationEnvelope,
  SurveyResponsesEnvelope,
  SurveySummaryEnvelope,
  SurveysListEnvelope,
  SurveysMetricsEnvelope,
  SurveyWriteBody,
} from "./surveys";

export const SURVEYS_BASE = "api/admin/surveys";

export type ListSurveysParams = {
  page: number;
  limit: number;
  search?: string;
  status?: string;
};

export type ListResponsesParams = ListSurveysParams;

/** Filters for the CSV export. Unlike the list, it has no paging — it streams every row. */
export type ExportResponsesParams = {
  /** ISO date; responses started on/after. */
  startDate?: string;
  /** ISO date; responses started on/before. */
  endDate?: string;
  /** Respondent name or email. */
  search?: string;
};

export async function fetchSurveys(
  params: ListSurveysParams,
  signal?: AbortSignal
): Promise<SurveysListEnvelope> {
  const { data } = await axiosInstance.get<SurveysListEnvelope>(SURVEYS_BASE, {
    params: compactParams(params),
    signal,
  });
  return data;
}

export async function fetchSurveysMetrics(
  signal?: AbortSignal
): Promise<SurveysMetricsEnvelope> {
  const { data } = await axiosInstance.get<SurveysMetricsEnvelope>(
    `${SURVEYS_BASE}/metrics`,
    { signal }
  );
  return data;
}

export async function fetchSurvey(
  id: string,
  signal?: AbortSignal
): Promise<SurveyDetailEnvelope> {
  const { data } = await axiosInstance.get<SurveyDetailEnvelope>(
    `${SURVEYS_BASE}/${id}`,
    { signal }
  );
  return data;
}

export async function fetchSurveySummary(
  id: string,
  signal?: AbortSignal
): Promise<SurveySummaryEnvelope> {
  const { data } = await axiosInstance.get<SurveySummaryEnvelope>(
    `${SURVEYS_BASE}/${id}/summary`,
    { signal }
  );
  return data;
}

export async function fetchSurveyResponses(
  id: string,
  params: ListResponsesParams,
  signal?: AbortSignal
): Promise<SurveyResponsesEnvelope> {
  const { data } = await axiosInstance.get<SurveyResponsesEnvelope>(
    `${SURVEYS_BASE}/${id}/responses`,
    { params: compactParams(params), signal }
  );
  return data;
}

/**
 * Streams the responses CSV. The server builds the file (one row per response, one column
 * per question key), so this only has to hand back the bytes for saving.
 */
export async function exportSurveyResponses(
  id: string,
  params: ExportResponsesParams = {},
  signal?: AbortSignal
): Promise<Blob> {
  const { data } = await axiosInstance.get<Blob>(
    `${SURVEYS_BASE}/${id}/responses/export`,
    { params: compactParams(params), responseType: "blob", signal }
  );
  return data;
}

export async function createSurvey(
  body: SurveyWriteBody
): Promise<SurveyMutationEnvelope> {
  const { data } = await axiosInstance.post<SurveyMutationEnvelope>(
    SURVEYS_BASE,
    body
  );
  return data;
}

export async function updateSurvey(
  id: string,
  body: Partial<SurveyWriteBody>
): Promise<SurveyMutationEnvelope> {
  const { data } = await axiosInstance.patch<SurveyMutationEnvelope>(
    `${SURVEYS_BASE}/${id}`,
    body
  );
  return data;
}

export async function publishSurvey(
  id: string
): Promise<SurveyMutationEnvelope> {
  const { data } = await axiosInstance.post<SurveyMutationEnvelope>(
    `${SURVEYS_BASE}/${id}/publish`,
    {}
  );
  return data;
}

export async function closeSurvey(id: string): Promise<SurveyMutationEnvelope> {
  const { data } = await axiosInstance.post<SurveyMutationEnvelope>(
    `${SURVEYS_BASE}/${id}/close`,
    {}
  );
  return data;
}

export async function deleteSurvey(
  id: string
): Promise<SurveyMutationEnvelope> {
  const { data } = await axiosInstance.delete<SurveyMutationEnvelope>(
    `${SURVEYS_BASE}/${id}`
  );
  return data;
}
